import { useTranslation } from "react-i18next";
import i18n from "src/i18n/config";
import { Partition } from "src/types/osbuilder";
import { z } from "zod";

import { FileContentType } from "@dashboard/files/types";
import { ExecutableModule, Product } from "@dashboard/products/types";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";

import { ModuleFileDialog, ModuleFileFormValues } from "../ModuleFileDialog";
import SharedUtils from "../ModulePanel.utils";

const schema = z.object({
    fileId: z.string({ message: i18n.t("common:validation.fieldRequired") }),
    partition: z.enum(["odm", "product", "system", "system_ext", "vendor"]),
});

type FormValues = ModuleFileFormValues & {
    partition: Partition;
};

type Props = {
    product: Product | null;
    module: ExecutableModule | null;
    open: boolean;
    onToggle: (value: boolean) => void;
};

const contentTypes = [FileContentType.Executable];

export const ExecutableDialog = (props: Props) => {
    const { t } = useTranslation(["modals"]);

    return (
        <ModuleFileDialog<FormValues, ExecutableModule>
            product={props.product}
            module={props.module}
            open={props.open}
            onToggle={props.onToggle}
            title={t("modals:manageProductModules.executable.title")}
            description={t("modals:manageProductModules.executable.description")}
            noFilesAvailable={t("modals:manageProductModules.executable.noFilesAvailable")}
            contentTypes={contentTypes}
            schema={schema}
            extraInitialValues={{ partition: props.module?.partition ?? "product" }}
            newModuleDefaults={{ type: "prebuilt_executable", tags: ["Own"] }}
            buildModuleValues={(file, sourceId, values) => ({
                name: file.filename,
                sourceId,
                partition: values.partition,
            })}
        >
            <ControlledSelectField
                name="partition"
                label={t("modals:manageProductModules.executable.input.partition.caption")}
                options={SharedUtils.partitionOptions}
            />
        </ModuleFileDialog>
    );
};
